// The interior kit in the game (issue #9, ?art=b only; Matt, 2026-10-21: "paint this properly in-game and get the sprites
// proper so the game looks like the previous samples").
//
// An authored room (client/public/art/b/kit/kit.json, written by tools/art/kit/install.py) is painted from reusable parts the
// way tools/art/kit/assemble.py and the settings console's room editor draw it, on the engine's own geometry:
//   - floors: each zone's texture on the real floor plane by the iso affine, one overlay per room in the plate layer (under
//     the blood decals and the opened-building shading);
//   - walls: every wall or partition FACE that faces into the room takes its finish up to the ceiling (dark above it), per
//     column, so a partition and the far wall behind it never share one image; outer faces keep route b, stubs keep theirs;
//   - furniture: each piece is its library sprite fitted to the projection (a box piece's bottom on its footprint's near
//     corner, a pedestal piece by its measured foot and top corners: catalogue "feet"), handed to iso_render to cut into
//     its 8 px depth strips. The bar is the venue's signature sprite over the counter and the back bar.
// The projection is the engine's: a cell (0.5 m) is 16 x 8 screen px, a metre up is Z_PX (26) px (toScreen).
import { CanvasSource, Container, Sprite, Texture } from 'pixi.js';
import { RES, SliceAtlas } from './paintblock';
import { IsoMap, Z_PX, structDef, type Prop } from '../../shared/src/iso';

interface KitTex { file: string; m: [number, number] }
interface KitVenue {
  bld: number; x0: number; y0: number; x1: number; y1: number; ceiling: number; floor: string; wall: string;
  zones: [number, number, number, number, string][]; zone_wall: Record<string, string>;
  bar?: { sprite?: string; h?: number; segment?: string; backbar?: string }; level?: number; floor_id?: number;
}
interface KitType { sprites?: { front?: string; back?: string; side?: string }; shape?: string; models?: Record<string, { front?: string; back?: string; side?: string }>; axis?: 'u' | 'v'; h?: number; base?: string; kind?: string }
interface Feet { foot: [number, number]; top_l: [number, number]; top_r: [number, number] }
interface V2 { anchor: [number, number]; gw: number; gh: number }
interface KitManifest { res: number; textures: Record<string, KitTex>; sprites: Record<string, string>; types: Record<string, KitType>; feet: Record<string, Feet>; v2?: Record<string, V2>; venues: Record<string, KitVenue> }
/** a fitted piece: its texture (logical px, RES texels) and where its top-left goes on the world screen */
export interface KitFit { tex: Texture; x: number; y: number; w: number; h: number; hM: number; fp?: [number, number, number, number]; cut?: number }
/** a back bar on the CAMERA side of its service aisle (the Tavern's, against the south wall: the bartender and the bar are
 *  north of it) is drawn cut at this height (metres), its lower cabinet and first shelf: at full height its lit shelves hid
 *  the bartender and the whole bar from the room (the game-studio skill's 'readability over physics': the camera-side back
 *  bar is cut at 1.3 m, like a near wall). One against a far wall (the Pint's, north) stands whole. */
const BACKBAR_CUT = 1.3;

/** cells -> world-screen logical px (toScreen of the cell corner, z metres up) */
const SP = (cx: number, cy: number, z = 0): [number, number] => [8 * (cx - cy), 4 * (cx + cy) - z * Z_PX];
const VOID = '#16110f';   // above a room's ceiling: the dark the room's light does not reach
/** the structure the kit paints on the inside (doors keep the engine's: their damage states are drawn there) */
const PAINTED = new Set(['wall', 'partition', 'storefront', 'window']);

export class InteriorKit {
  m: KitManifest | null = null;
  ok = false;
  imgs = new Map<string, HTMLImageElement>();
  atlas = new SliceAtlas();
  floors = new Container();
  private byBld = new Map<number, KitVenue>();
  private sources: CanvasSource[] = [];

  async load(base = 'art/b/kit/') {
    try {
      const r = await fetch(base + 'kit.json'); if (!r.ok) return;
      const m: KitManifest = await r.json(); this.m = m;
      const files = new Set([...Object.values(m.textures).map(t => t.file), ...Object.values(m.sprites)]);
      // onload, not decode() (iOS Safari: paintblock.ts)
      await Promise.all([...files].map(f => new Promise<void>((res, rej) => {
        const im = new Image(); im.onload = () => { this.imgs.set(f, im); res(); }; im.onerror = () => rej(new Error('kit image ' + f)); im.src = base + f;
      })));
      for (const v of Object.values(m.venues)) if (!v.level) this.byBld.set(v.bld, v);
      this.ok = true;
    } catch (e) { console.warn('interiorkit: not loaded', e); this.ok = false; }
  }

  venue(bld: number): KitVenue | undefined { return this.ok ? this.byBld.get(bld) : undefined; }
  /** the kit for one upper floor (the floor system: its own map in local cells, its own renderer): the same images and atlas,
   *  only that floor's venues; null when no kit room is up there */
  forFloor(fid: number): InteriorKit | null {
    if (!this.ok) return null;
    const vs = Object.values(this.m!.venues).filter(v => v.floor_id === fid); if (!vs.length) return null;
    const k = Object.create(InteriorKit.prototype) as InteriorKit;
    Object.assign(k, { m: { ...this.m!, venues: Object.fromEntries(Object.entries(this.m!.venues).filter(([, v]) => v.floor_id === fid)) },
      ok: true, imgs: this.imgs, atlas: this.atlas, floors: new Container(), byBld: new Map(vs.map(v => [v.bld, v])), sources: [] });
    return k;
  }
  private tex(key: string) { const t = this.m!.textures[key]; const im = t && this.imgs.get(t.file); return im ? { im, t } : null; }
  private zoneAt(v: KitVenue, cx: number, cy: number): string { for (let k = v.zones.length - 1; k >= 0; k--) { const z = v.zones[k]; if (cx >= z[0] && cx < z[2] && cy >= z[1] && cy < z[3]) return z[4]; } return v.floor; }

  /** the painted floor of every kit room: one overlay sprite each, in world-screen px (iso_render adds them to the plate layer) */
  buildFloors(map: IsoMap) {
    if (!this.ok) return;
    for (const v of Object.values(this.m!.venues)) {
      const left = SP(v.x0, v.y1)[0], right = SP(v.x1, v.y0)[0], top = SP(v.x0, v.y0)[1], bot = SP(v.x1, v.y1)[1];
      const W = Math.ceil((right - left) * RES), H = Math.ceil((bot - top) * RES);
      const cv = document.createElement('canvas'); cv.width = W; cv.height = H; const g = cv.getContext('2d')!;
      const fill = (key: string, x0: number, y0: number, x1: number, y1: number) => {
        const T = this.tex(key); if (!T) return;
        const ppx = T.im.width / T.t.m[0], ppy = T.im.height / T.t.m[1];   // texture px per metre
        const pat = g.createPattern(T.im, 'repeat')!;
        // texture px -> canvas px: u = tx/ppx m = 2 tx/ppx cells along x, v likewise along y, through SP, minus the corner
        const ux = 2 / ppx, vy = 2 / ppy;
        pat.setTransform(new DOMMatrix([8 * ux * RES, 4 * ux * RES, -8 * vy * RES, 4 * vy * RES, (SP(v.x0, v.y0)[0] - left) * RES, (SP(v.x0, v.y0)[1] - top) * RES]));
        g.fillStyle = pat; g.beginPath();
        for (const [a, b] of [[x0, y0], [x1, y0], [x1, y1], [x0, y1]] as [number, number][]) { const p = SP(a, b); g.lineTo((p[0] - left) * RES, (p[1] - top) * RES); }
        g.closePath(); g.fill();
      };
      fill(v.floor, v.x0, v.y0, v.x1, v.y1);
      for (const z of v.zones) fill(z[4], z[0], z[1], z[2], z[3]);
      // (a 'clear' zone - the Black Dog's stairs - keeps the engine's own ground: cut out of the painted floor)
      g.globalCompositeOperation = 'destination-out';
      for (const z of v.zones) if (z[4] === 'clear') { g.beginPath(); for (const [a, b] of [[z[0], z[1]], [z[2], z[1]], [z[2], z[3]], [z[0], z[3]]] as [number, number][]) { const p = SP(a, b); g.lineTo((p[0] - left) * RES, (p[1] - top) * RES); } g.closePath(); g.fill(); }
      g.globalCompositeOperation = 'source-over';
      // only where the room has floor: a structure cell is the wall's (its column covers it), and outside the unit is the street's
      g.globalCompositeOperation = 'destination-in'; g.beginPath();
      for (let cy = v.y0; cy < v.y1; cy++) for (let cx = v.x0; cx < v.x1; cx++) {
        const i = cx + cy * map.w; if (map.b[i] !== v.bld) continue;
        const q = [SP(cx, cy), SP(cx + 1, cy), SP(cx + 1, cy + 1), SP(cx, cy + 1)];
        g.moveTo((q[0][0] - left) * RES, (q[0][1] - top) * RES); for (let k = 1; k < 4; k++) g.lineTo((q[k][0] - left) * RES, (q[k][1] - top) * RES); g.closePath();
      }
      g.fill(); g.globalCompositeOperation = 'source-over';
      const src = new CanvasSource({ resource: cv, resolution: RES, scaleMode: 'linear', autoGenerateMipmaps: true }); this.sources.push(src);
      const s = new Sprite(new Texture({ source: src })); s.x = left; s.y = top; this.floors.addChild(s);
    }
  }

  /** iso_render's hook for a wall column of a kit room (after route b's): the faces it shows into the room take the room's
   *  finish. The column sprite is CW (16) x hp + CH + 1 px, its top-left at the cell's north corner minus hp; its left half
   *  is the south face, its right half the east face (the two the camera sees). Returns whether it painted. */
  /** which of a structure cell's two camera-facing faces (south, east) look into its kit room's floor */
  facesIn(map: IsoMap, cx: number, cy: number): { S: boolean; E: boolean; v: KitVenue } | null {
    const i = cx + cy * map.w, v = this.venue(map.b[i]); if (!v || !map.s[i]) return null;
    const key = structDef(map.s[i]).key; if (!PAINTED.has(key)) return null;
    const into = (x: number, y: number) => map.inside(x, y) && map.b[x + y * map.w] === v.bld && !map.s[x + y * map.w];
    const S = into(cx, cy + 1), E = into(cx + 1, cy);
    return S || E ? { S, E, v } : null;
  }

  wall(spr: Sprite, map: IsoMap, cx: number, cy: number, hM: number): boolean {
    const fi = this.facesIn(map, cx, cy); if (!fi) return false;
    const { S, E, v } = fi, key = structDef(map.s[cx + cy * map.w]).key;
    const t = spr.texture, f = t.frame, src = t.source.resource as CanvasImageSource | undefined; if (!src) return false;
    const hp = Math.round(hM * Z_PX), W = Math.round(f.width * RES), H = Math.round(f.height * RES), zc = Math.min(hM, v.ceiling);
    const nt = this.atlas.add(W, H, g => {
      g.imageSmoothingEnabled = false; g.drawImage(src, f.x, f.y, f.width, f.height, 0, 0, W, H);   // today's column (its top and shape)
      g.imageSmoothingEnabled = true;
      // a face from screen L0 to L1 at its foot (logical, column-local), its finish running s0 .. s0 + 0.5 m left to right
      const face = (L0: [number, number], L1: [number, number], s0: number, finish: string, shade: number) => {
        const T = this.tex(finish); if (!T) return;
        const ppx = T.im.width / T.t.m[0], ppy = T.im.height / T.t.m[1], dx = L1[0] - L0[0], dy = L1[1] - L0[1];
        const quad = (z0: number, z1: number) => { g.beginPath(); g.moveTo(L0[0] * RES, (L0[1] - z0 * Z_PX) * RES); g.lineTo(L1[0] * RES, (L1[1] - z0 * Z_PX) * RES);
          g.lineTo(L1[0] * RES, (L1[1] - z1 * Z_PX) * RES); g.lineTo(L0[0] * RES, (L0[1] - z1 * Z_PX) * RES); g.closePath(); };
        const pat = g.createPattern(T.im, 'repeat')!;
        // texture (tx, ty) -> s = tx/ppx m along the face, z = top - ty/ppy m up: x = L0x + dx (s - s0)/0.5, y = L0y + dy (s - s0)/0.5 - z Z_PX
        const ztop = finish === v.wall ? v.ceiling : T.t.m[1];   // the main finish is painted floor to ceiling; a tiled one repeats
        pat.setTransform(new DOMMatrix([dx / (0.5 * ppx) * RES, dy / (0.5 * ppx) * RES, 0, Z_PX / ppy * RES,
          (L0[0] - dx * s0 / 0.5) * RES, (L0[1] - dy * s0 / 0.5 - ztop * Z_PX) * RES]));
        g.save(); quad(0, zc); g.fillStyle = pat; g.fill();
        if (key === 'storefront' || key === 'window') {
          // glass seen from inside at night: dark blue-black with a faint sheen, steel mullions every 1.2 m; a storefront
          // keeps a 0.45 m base of the wall's lower finish, a window sits 0.9-2.2 m up in the wall
          const g0 = key === 'storefront' ? 0.45 : 0.9, g1 = key === 'storefront' ? zc - 0.12 : Math.min(2.2, zc - 0.2);
          quad(g0, g1); const gr = g.createLinearGradient(0, (L0[1] - g1 * Z_PX) * RES, 0, (L0[1] - g0 * Z_PX) * RES);
          gr.addColorStop(0, '#0b1422'); gr.addColorStop(0.55, '#14233a'); gr.addColorStop(1, '#0d1626'); g.fillStyle = gr; g.fill();
          g.save(); g.clip(); g.strokeStyle = 'rgba(150,180,215,0.10)'; g.lineWidth = 2 * RES;
          for (let k = -2; k < 4; k++) { const x = (L0[0] + k * 6) * RES; g.beginPath(); g.moveTo(x, (L0[1] - g0 * Z_PX) * RES); g.lineTo(x + 10 * RES, (L0[1] - g1 * Z_PX - 10) * RES); g.stroke(); }
          g.restore();
          const m0 = Math.ceil(s0 / 1.2) * 1.2;
          if (m0 < s0 + 0.5) { const k = (m0 - s0) / 0.5, x = L0[0] + dx * k, y = L0[1] + dy * k; g.fillStyle = '#2a2d31';
            g.fillRect((x - 0.6) * RES, (y - g1 * Z_PX) * RES, 1.2 * RES, (g1 - g0) * Z_PX * RES); }
        }
        if (shade < 1) { quad(0, zc); g.fillStyle = `rgba(0,0,0,${1 - shade})`; g.fill(); }
        g.restore();
        if (hM > zc + 0.01) { g.save(); quad(zc, hM); g.fillStyle = VOID; g.fill(); g.restore(); }
      };
      const fin = (x: number, y: number) => { const z = this.zoneAt(v, x, y); return z === v.floor ? v.wall : (v.zone_wall[z] ?? v.wall); };
      // the south face: from the cell's west corner (0, hp + 4) to its south corner (8, hp + 8); s runs east (u)
      if (S) face([0, hp + 4], [8, hp + 8], (cx - v.x0) * 0.5, fin(cx, cy + 1), 1);
      // the east face: from the south corner (8, hp + 8) to the east corner (16, hp + 4); s runs north (screen left to right)
      if (E) face([8, hp + 8], [16, hp + 4], (v.y1 - cy - 1) * 0.5, fin(cx + 1, cy), 0.86);
    });
    spr.texture = nt;
    return true;
  }

  /** whether the kit draws this prop (iso_render then skips today's art for every cell of it) */
  handles(p: Prop): boolean {
    if (!this.ok || !p.venue || !this.m!.venues[p.venue] || p.t === 'tv') return false;
    // (a kit for the street does not draw an upper floor's venue, and the reverse: the venue entry says which map it is on)
    const ty = this.m!.types[p.t]; return !!ty && (!!ty.sprites?.front || p.t === 'bar' || p.t === 'backbar');
  }

  /** the piece fitted to the projection, or null for one drawn by another piece (the back bar is the bar's sprite) */
  fit(p: Prop, map: IsoMap): KitFit[] {
    const v = this.m!.venues[p.venue!];
    // a bar run in a venue drawn from SEGMENTS (the Beercade's island): 1 m pieces along its long axis, each placed exactly
    const seg = p.t === 'bar' ? v.bar?.segment : p.t === 'backbar' ? v.bar?.backbar : undefined;
    if (seg) {
      const long = p.w >= p.d, n = Math.max(1, Math.round((long ? p.w : p.d) / 2)), out: KitFit[] = [];
      // (the service aisle up-screen of a back bar: its bar lies north of it (a run along x) or west (along y))
      const bar = p.t === 'backbar' ? map.props.find(q => q.venue === p.venue && q.t === 'bar' && !q.dead) : undefined;
      const cut = bar && (long ? bar.y + bar.d <= p.y : bar.x + bar.w <= p.x) ? BACKBAR_CUT : undefined;
      for (let k = 0; k < n; k++) {
        const a = Math.round(k * (long ? p.w : p.d) / n), b = Math.round((k + 1) * (long ? p.w : p.d) / n);
        const q = { ...p, ...(long ? { x: p.x + a, w: b - a } : { y: p.y + a, d: b - a }) } as Prop;
        const f = this.fitOne(q, map, seg, !long); if (f) out.push({ ...f, fp: [q.x, q.y, q.x + q.w, q.y + q.d], cut, hM: cut ?? f.hM });
      }
      return out;
    }
    // (a booth run longer than one booth - the Pint's back-room benches, 2 m on a 1.5 m booth - is drawn as one booth per
    // metre or so along its run, so the bench reads as a run of booths, not one booth with gaps at both ends)
    if (p.t === 'booth' && Math.max(p.w, p.d) >= 4) {
      const long = p.w >= p.d, L = long ? p.w : p.d, n = Math.max(2, Math.round(L / 3)), out: KitFit[] = [];
      for (let k = 0; k < n; k++) {
        const a = Math.round(k * L / n), b = Math.round((k + 1) * L / n);
        const q = { ...p, ...(long ? { x: p.x + a, w: b - a } : { y: p.y + a, d: b - a }) } as Prop;
        const f = this.fitOne(q, map); if (f) out.push({ ...f, fp: [q.x, q.y, q.x + q.w, q.y + q.d] });
      }
      return out;
    }
    const f = this.fitOne(p, map); return f ? [f] : [];
  }

  private fitOne(p: Prop, map: IsoMap, forceName = '', forceFlip = false): KitFit | null {
    const m = this.m!, v = m.venues[p.venue!], ty = m.types[p.t];
    let x0 = p.x, y0 = p.y, x1 = p.x + p.w, y1 = p.y + p.d, h = ty.h ?? 1, name = '', flip = false;
    if (forceName) { name = forceName; flip = forceFlip; }
    else if (p.t === 'backbar') return null;
    else if (p.t === 'bar') {
      if (!v.bar?.sprite) return null;
      const bb = map.props.find(q => q.venue === p.venue && q.t === 'backbar');
      if (bb) { x0 = Math.min(x0, bb.x); y0 = Math.min(y0, bb.y); x1 = Math.max(x1, bb.x + bb.w); y1 = Math.max(y1, bb.y + bb.d); }
      name = v.bar.sprite; h = v.bar.h ?? 1.25;
    } else {
      const model = (p as any).model as string | undefined;
      const sp = (model && ty.models?.[model]) || ty.sprites!, f = (p.face ?? 'S').toUpperCase();
      if (sp.side) {
        // an L piece (the corner booth, tools/art/kit/room_layout.py seat_cells): face E = its corner far (north-west, the
        // 'front' view), W = its corner near (south-east, 'back'), N = corner south-west ('side'), S = that view mirrored
        if (f === 'E') name = sp.front!; else if (f === 'W') name = sp.back ?? sp.front!; else { name = sp.side; flip = f === 'S'; }
      }
      else if (ty.axis && !sp.back) { name = sp.front!; flip = (p.w > p.d) !== (ty.axis === 'u'); }   // a one-view piece mirrors by its long axis
      else if ((f === 'W' || f === 'N') && sp.back) { name = sp.back; flip = f === 'N'; }
      else { name = sp.front!; flip = f === 'S' || f === 'N'; }
    }
    const im = this.imgs.get(m.sprites[name] ?? ''); if (!im) return null;
    const v2 = m.v2?.[name];
    if (v2) {
      // format v2 (tools/art/kit/maquette.py + paint_v2.py): painted over a real-size model, so it is placed exactly - its
      // anchor (the floor under its footprint's centre) on the footprint's centre, its size in game px; mirrored about the anchor
      const c = SP((x0 + x1) / 2, (y0 + y1) / 2, 0), gw = v2.gw, gh = v2.gh;
      const bx = Math.floor(c[0] - (flip ? 1 - v2.anchor[0] : v2.anchor[0]) * gw), by = Math.floor(c[1] - v2.anchor[1] * gh);
      const ox = c[0] - (flip ? 1 - v2.anchor[0] : v2.anchor[0]) * gw - bx, oy = c[1] - v2.anchor[1] * gh - by;
      const W = Math.ceil(gw + ox), H = Math.ceil(gh + oy);
      const tex = this.atlas.add(W * RES, H * RES, g => {
        g.imageSmoothingEnabled = true; g.imageSmoothingQuality = 'high';
        if (flip) { g.translate((ox + gw) * RES, oy * RES); g.scale(-1, 1); } else g.translate(ox * RES, oy * RES);
        g.drawImage(im, 0, 0, gw * RES, gh * RES); g.setTransform(1, 0, 0, 1, 0, 0);
      });
      return { tex, x: bx, y: by, w: W, h: H, hM: h };
    }
    const iw = im.width, ih = im.height, ft = m.feet[name];
    let M: [number, number, number, number, number, number];         // sprite px -> world-screen logical px (a, b, c, d, e, f)
    if (ft) {
      // a pedestal piece: the affine that puts its foot under the footprint's centre and its top's corners on the footprint's
      // corners at its height (the editor's drawFitted)
      const src = [ft.top_l, ft.top_r, ft.foot].map(q => [q[0] * iw, q[1] * ih]);
      const tl = SP(x0, y1, h), tr = SP(x1, y0, h), tf = SP((x0 + x1) / 2, (y0 + y1) / 2, 0);
      M = affine(src as [number, number][], flip ? [tr, tl, tf] : [tl, tr, tf]);
    } else {
      // a box piece: scaled to its footprint's screen box, its bottom on the footprint's near corner
      const xs: number[] = [], ys: number[] = [];
      for (const a of [x0, x1]) for (const b of [y0, y1]) for (const z of [0, h]) { const q = SP(a, b, z); xs.push(q[0]); ys.push(q[1]); }
      const bw = Math.max(...xs) - Math.min(...xs), bh = Math.max(...ys) - Math.min(...ys);
      const s = Math.min(bw / iw, bh / ih * 1.12), w = iw * s, hh = ih * s;
      const left = (Math.max(...xs) + Math.min(...xs)) / 2 - w / 2, top = SP(x1, y1)[1] - hh;
      M = flip ? [-s, 0, 0, s, left + w, top] : [s, 0, 0, s, left, top];
    }
    const cs = [[0, 0], [iw, 0], [0, ih], [iw, ih]].map(([x, y]) => [M[0] * x + M[2] * y + M[4], M[1] * x + M[3] * y + M[5]]);
    const bx = Math.floor(Math.min(...cs.map(c => c[0]))), by = Math.floor(Math.min(...cs.map(c => c[1])));
    const bw = Math.ceil(Math.max(...cs.map(c => c[0]))) - bx, bh = Math.ceil(Math.max(...cs.map(c => c[1]))) - by;
    const tex = this.atlas.add(bw * RES, bh * RES, g => {
      g.imageSmoothingEnabled = true; g.imageSmoothingQuality = 'high';
      g.setTransform(M[0] * RES, M[1] * RES, M[2] * RES, M[3] * RES, (M[4] - bx) * RES, (M[5] - by) * RES);
      g.drawImage(im, 0, 0); g.setTransform(1, 0, 0, 1, 0, 0);
    });
    return { tex, x: bx, y: by, w: bw, h: bh, hM: h };
  }

  /** new slices go up (once a frame, like route b's) */
  sync() { this.atlas.flush(); }
}

/** the affine (canvas a, b, c, d, e, f) taking three source points to three destination points */
function affine(s: [number, number][], d: [number, number][]): [number, number, number, number, number, number] {
  const det = (m: number[][]) => m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) - m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) + m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0]);
  const A = s.map(p => [p[0], p[1], 1]), D = det(A);
  const solve = (t: number[]) => [0, 1, 2].map(c => det(A.map((r, i) => { const x = r.slice(); x[c] = t[i]; return x; })) / D);
  const kx = solve(d.map(p => p[0])), ky = solve(d.map(p => p[1]));
  return [kx[0], ky[0], kx[1], ky[1], kx[2], ky[2]];
}
